<template>
  <div class="rc">
    <!-- Nav -->
    <header class="c-nav">
      <div class="c-nav-brand" @click="$router.push('/')">
        <span class="c-logo">☕</span>
        <span>The Coffee Break</span>
      </div>
      <nav class="c-nav-links">
        <a @click="$router.push('/')">Today</a>
        <a @click="$router.push('/about')">About</a>
        <a @click="$router.push('/ilovetcb')">Support</a>
      </nav>
      <button class="c-nav-cta" @click="scrollToSubscribe">Subscribe</button>
    </header>

    <!-- Hero -->
    <section class="c-hero">
      <div class="c-hero-grid">
        <div class="c-hero-left">
          <h1 class="c-h1">Stay informed, <em>be smarter in 3 minutes.</em></h1>
          <p class="c-lede">
            A daily email for Malaysians who want to stay informed without the
            doomscrolling. 1 issue, 5 times a week, and it's done before your
            kopi goes cold.
          </p>
          <div class="c-cta" ref="subscribeRef">
            <div class="c-field" :class="{ 'c-field--focus': heroPinned }">
              <input
                v-model="email"
                placeholder="you@email.com"
                type="email"
                @focus="heroPinned = true"
                @blur="heroPinned = false"
                @keyup.enter="subscribe"
              />
              <button @click="subscribe" :disabled="loading">
                {{ loading ? "Subscribing…" : "Subscribe for FREE" }}
              </button>
            </div>
            <div ref="turnstileHero"></div>
            <p v-if="successMsg" class="c-inline-msg c-inline-msg--ok">
              {{ successMsg }}
            </p>
            <p v-if="errorMsg" class="c-inline-msg c-inline-msg--err">
              {{ errorMsg }}
            </p>
          </div>
        </div>

        <div class="c-hero-right">
          <div class="c-mascot-wrap">
            <div class="c-steam" aria-hidden="true">
              <span class="c-steam-puff c-steam-puff--1"></span>
              <span class="c-steam-puff c-steam-puff--2"></span>
              <span class="c-steam-puff c-steam-puff--3"></span>
              <span class="c-steam-puff c-steam-puff--4"></span>
              <span class="c-steam-puff c-steam-puff--5"></span>
            </div>
            <img
              src="../assets/cup-mascot-news.png"
              alt="Coffee cup mascot reading the morning paper"
              class="c-hero-mascot"
            />
          </div>
        </div>
      </div>
    </section>

    <!-- Mission statement -->
    <section class="c-promise">
      <div class="c-promise-mission">
        <p>Our goal is simple — to make our readers <em>more informed</em>. We filter the news to take out the noise and at the same time, our readers can know and learn more.</p>
        <p>We cover <span class="c-hl-blue">interesting facts</span>, <span class="c-hl-coral">local</span> and <span class="c-hl-yellow">global</span> news all in our daily newsletter.</p>
      </div>
    </section>

    <!-- What's inside -->
    <section class="c-today">
      <div class="c-section-head">
        <div>
          <div class="c-eyebrow">Latest issue</div>
          <h2>What's inside.</h2>
        </div>
        <div class="c-total">Total read · 3:00</div>
      </div>

      <a class="c-lead" :href="stories[0].link" target="_blank" rel="noopener">
        <div
          class="c-lead-img"
          :style="{ backgroundImage: `url(${stories[0].img})` }"
        ></div>
        <div class="c-lead-body">
          <div class="c-row">
            <span class="c-kicker">01 · {{ stories[0].kicker }}</span>
            <span class="c-time">1:00</span>
          </div>
          <h3>{{ stories[0].title }}</h3>
          <p>{{ stories[0].dek }}</p>
          <span class="c-link">Read the story →</span>
        </div>
      </a>

      <div class="c-feed">
        <a
          v-for="(story, i) in stories.slice(1, 5)"
          :key="i"
          class="c-card"
          :href="story.link"
          target="_blank"
          rel="noopener"
        >
          <div class="c-row">
            <span class="c-kicker c-kicker--alt"
              >{{ String(i + 2).padStart(2, "0") }} · {{ story.kicker }}</span
            >
            <span class="c-time c-time--sm"
              >0:{{ (45 - i * 5).toString().padStart(2, "0") }}</span
            >
          </div>
          <h4>{{ story.title }}</h4>
          <p>{{ story.dek }}</p>
        </a>
      </div>
    </section>

    <!-- Breakdown -->
    <section class="c-breakdown">
      <div class="c-eyebrow" style="margin-bottom: 8px">The structure</div>
      <h2>Your three minutes, in three parts.</h2>
      <div class="c-break-grid">
        <div class="c-break">
          <div class="c-break-head">
            <span
              class="c-break-time"
              style="background: #eaf4fe; color: #0072b2"
              >00:00 — 01:00</span
            >
            <span class="c-break-tag">Lead</span>
          </div>
          <h3>The day's must-know</h3>
          <p>
            One headline. Three paragraphs. Facts first, context second,
            implications last.
          </p>
        </div>
        <div class="c-break">
          <div class="c-break-head">
            <span
              class="c-break-time"
              style="background: #fef5db; color: #8a6500"
              >01:00 — 02:00</span
            >
            <span class="c-break-tag">Malaysia &amp; markets</span>
          </div>
          <h3>What moved, what mattered</h3>
          <p>
            Politics, policy, the ringgit, regional news. Numbers where numbers
            belong.
          </p>
        </div>
        <div class="c-break">
          <div class="c-break-head">
            <span
              class="c-break-time"
              style="background: #fdeae5; color: #a63318"
              >02:00 — 03:00</span
            >
            <span class="c-break-tag">Briefs</span>
          </div>
          <h3>Quote, chart, laugh</h3>
          <p>
            A line you'll repeat at lunch, a chart worth screenshotting, a small
            thing that'll make you smile.
          </p>
        </div>
      </div>
    </section>

    <!-- Stats band -->
    <section class="c-stats">
      <div>
        <b>20,341</b><span>Daily readers</span>
      </div>
      <div><b>247</b><span>Issues shipped</span></div>
      <div>
        <b>4.9<em>/5</em></b
        ><span>Reader rating</span>
      </div>
      <div><b>2:47</b><span>Avg read time</span></div>
    </section>

    <!-- Testimonials -->
    <section class="c-testi">
      <div class="c-eyebrow" style="margin-bottom: 8px">From the inbox</div>
      <h2>Readers, unprompted.</h2>
      <div class="c-testi-grid">
        <figure
          v-for="(t, i) in testimonials"
          :key="t.user"
          class="c-testi-card"
        >
          <div class="c-quote-mark" :style="{ color: accentColors[i] }">"</div>
          <blockquote>{{ t.content }}</blockquote>
          <figcaption>
            <b>{{ t.user }}</b>
            <span>{{ t.role }}</span>
          </figcaption>
        </figure>
      </div>
    </section>

    <!-- Final CTA -->
    <section class="c-final">
      <div class="c-final-inner">
        <h2>
          Three minutes. Five mornings a week.<br /><em>Start tomorrow.</em>
        </h2>
        <div class="c-field c-field--light">
          <input
            v-model="emailFinal"
            placeholder="you@email.com"
            type="email"
            @keyup.enter="subscribeFinal"
          />
          <button @click="subscribeFinal" :disabled="loadingFinal">
            {{ loadingFinal ? "Subscribing…" : "Subscribe for FREE" }}
          </button>
        </div>
        <div ref="turnstileFinal"></div>
        <p
          v-if="successMsgFinal"
          class="c-inline-msg c-inline-msg--ok"
          style="margin-top: 12px"
        >
          {{ successMsgFinal }}
        </p>
        <div class="c-final-meta">
          Free forever · Unsubscribe any time · Tip jar, not a paywall
        </div>
      </div>
    </section>

    <!-- Footer -->
    <footer class="c-foot">
      <div class="c-foot-brand" @click="$router.push('/')">
        <span class="c-logo">☕</span>
        <span>The Coffee Break</span>
      </div>
      <nav>
        <a @click="$router.push('/about')">About</a>
        <a @click="$router.push('/ilovetcb')">Support us</a>
        <a @click="$router.push('/career')">Careers</a>
        <a @click="$router.push('/privacy-policy')">Privacy</a>
        <a @click="$router.push('/terms-of-service')">Terms</a>
      </nav>
      <div class="c-foot-legal">© 2026 Finlit Media Tech Group Sdn Bhd</div>
    </footer>
  </div>
</template>

<script>
import axios from "axios";
import { renderTurnstile, resetTurnstile } from "../turnstile";

const SUBSCRIBE_URL =
  "https://hukopzhug3.execute-api.ap-southeast-1.amazonaws.com/production/email/submit";

export default {
  name: "LandingClean",
  data() {
    return {
      email: "",
      emailFinal: "",
      loading: false,
      loadingFinal: false,
      successMsg: "",
      errorMsg: "",
      successMsgFinal: "",
      turnstileHeroToken: "",
      turnstileFinalToken: "",
      turnstileHeroId: null,
      turnstileFinalId: null,
      heroPinned: false,
      accentColors: ["#0093E9", "#F4B400", "#E3563C"],
      stories: [
        {
          kicker: "National",
          title:
            "LHDN to gain access to Malaysians' bank accounts without prior consent",
          dek: "The taxman's new powers explained — what it means for your savings, your business, and your privacy rights under the updated Income Tax Act.",
          img: "https://cdn.substack.com/image/youtube/w_728,c_limit/ywV5JMDHiAY",
          link: "https://thecoffeebreak.substack.com",
        },
        {
          kicker: "Economy",
          title: "Vegetable prices in Malaysia hit hyperinflation levels",
          dek: "Supply chain disruptions and monsoon flooding push tomatoes and leafy greens to record retail prices.",
          img: "https://cdn.substack.com/image/fetch/w_336,c_limit,f_auto,q_auto:good,fl_progressive:steep/https%3A%2F%2Fbucketeer-e05bbc84-baa3-437e-9518-adb32be77984.s3.amazonaws.com%2Fpublic%2Fimages%2F76a0a55c-bb9b-4f87-8d07-46213de3e743_627x680.jpeg",
          link: "https://thecoffeebreak.substack.com",
        },
        {
          kicker: "Politics",
          title: "PM moves to cap chicken retail price — while dining on Evian",
          dek: "Cabinet approves a 20 sen reduction to the maximum retail chicken price amid cost-of-living pressure.",
          img: "https://bucketeer-e05bbc84-baa3-437e-9518-adb32be77984.s3.amazonaws.com/public/images/61e88e3f-63e5-4dd3-b2c9-adc6bb80c0f2_970x940.png",
          link: "https://thecoffeebreak.substack.com",
        },
        {
          kicker: "Finance",
          title:
            "3 major Malaysian banks gave USD 600M unsecured loan to failed Genting HK",
          dek: "How local lenders ended up exposed — and what regulators are now demanding in response.",
          img: "https://cdn.substack.com/image/fetch/h_600,c_limit,f_auto,q_auto:good,fl_progressive:steep/https%3A%2F%2Fbucketeer-e05bbc84-baa3-437e-9518-adb32be77984.s3.amazonaws.com%2Fpublic%2Fimages%2Fb5735457-9c5d-47fa-a75c-785c07fa0dd4_4032x3024.jpeg",
          link: "https://thecoffeebreak.substack.com",
        },
        {
          kicker: "Governance",
          title: "Parliament tables bill to cap PM tenure at 10 years",
          dek: "A constitutional amendment to limit prime ministerial terms moves to the floor for its first reading.",
          img: "https://cdn.substack.com/image/fetch/h_600,c_limit,f_auto,q_auto:good,fl_progressive:steep/https%3A%2F%2Fpbs.substack.com%2Fmedia%2FFKPadvfUYAA-GvE.jpg",
          link: "https://thecoffeebreak.substack.com",
        },
      ],
      testimonials: [
        {
          content:
            "I spend a LOT of time online and I still miss half the news in this curated newsletter. Go subscribe to The Coffee Break!",
          user: "@surayaror",
          role: "Financial Blogger",
        },
        {
          content:
            "Received my 2nd newsletter for @BreakMyCoffee over coffee. Love it, takes out all the noise in the news. Highly recommend you to subscribe to them & stay informed about latest news.",
          user: "@shannonchowz",
          role: "Reader since 2021",
        },
        {
          content: "Their newsletter is great stuff seriously. Do subscribe!",
          user: "@bitcoinmalaya",
          role: "Crypto & markets",
        },
      ],
    };
  },
  mounted() {
    this.$nextTick(async () => {
      this.turnstileHeroId = await renderTurnstile(
        this.$refs.turnstileHero,
        (token) => { this.turnstileHeroToken = token; }
      );
      this.turnstileFinalId = await renderTurnstile(
        this.$refs.turnstileFinal,
        (token) => { this.turnstileFinalToken = token; }
      );
    });
  },
  methods: {
    _resetTurnstile(isHero) {
      if (isHero) {
        resetTurnstile(this.turnstileHeroId);
        this.turnstileHeroToken = "";
      } else {
        resetTurnstile(this.turnstileFinalId);
        this.turnstileFinalToken = "";
      }
    },
    scrollToSubscribe() {
      this.$refs.subscribeRef.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      this.$refs.subscribeRef.querySelector("input").focus();
    },
    async subscribe() {
      await this._doSubscribe("hero");
    },
    async subscribeFinal() {
      await this._doSubscribe("final");
    },
    async _doSubscribe(source) {
      const isHero = source === "hero";
      const emailVal = isHero ? this.email.trim() : this.emailFinal.trim();

      if (isHero) {
        this.successMsg = "";
        this.errorMsg = "";
      } else {
        this.successMsgFinal = "";
      }

      if (!emailVal) {
        if (isHero) this.errorMsg = "Please enter your email.";
        return;
      }
      if (emailVal.endsWith(".con")) {
        if (isHero) this.errorMsg = "That email looks off — did you mean .com?";
        return;
      }

      if (isHero) this.loading = true;
      else this.loadingFinal = true;

      try {
        this.$gtag.event("subscribe", {
          event_label: "subscribe_clean",
          value: emailVal,
        });
        await axios.post(SUBSCRIBE_URL, {
          email: emailVal,
          referral: "",
          token: isHero ? this.turnstileHeroToken : this.turnstileFinalToken,
        });
        this._resetTurnstile(isHero);
        if (isHero) {
          this.successMsg = "You're in! Check your inbox for a welcome email.";
          this.email = "";
        } else {
          this.successMsgFinal =
            "You're in! Check your inbox for a welcome email.";
          this.emailFinal = "";
        }
      } catch (err) {
        this._resetTurnstile(isHero);
        if (isHero)
          this.errorMsg = err.response && err.response.status === 403
            ? "We could not verify that you are human. Please try again."
            : "Something went wrong — please try again :(";
      } finally {
        if (isHero) this.loading = false;
        else this.loadingFinal = false;
      }
    },
  },
};
</script>

<style>
@import url("https://fonts.googleapis.com/css2?family=Inter+Tight:ital,wght@0,400;0,500;0,600;0,700;1,400&family=Playfair+Display:ital,wght@0,700;0,800;1,700;1,800&family=JetBrains+Mono:wght@700&display=swap");
</style>

<style scoped>
.rc {
  font-family: "Inter Tight", "Helvetica Neue", sans-serif;
  background: #ffffff;
  color: #0e1524;
  -webkit-font-smoothing: antialiased;
}
.rc *,
.rc *::before,
.rc *::after {
  box-sizing: border-box;
}

/* ── Nav ── */
.c-nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 48px;
  border-bottom: 1px solid #edeff3;
  background: #fff;
  position: sticky;
  top: 0;
  z-index: 20;
  backdrop-filter: saturate(1.2);
}
.c-nav-brand {
  display: flex;
  gap: 10px;
  align-items: center;
  font-weight: 700;
  font-size: 16px;
  letter-spacing: -0.005em;
  cursor: pointer;
}
.c-logo {
  font-size: 20px;
}
.c-nav-links {
  display: flex;
  gap: 32px;
  font-size: 14px;
  font-weight: 500;
  color: #4a5266;
}
.c-nav-links a {
  cursor: pointer;
  color: inherit;
  text-decoration: none;
}
.c-nav-links a:hover {
  color: #0e1524;
}
.c-nav-cta {
  background: #0093e9;
  color: #fff;
  border: 0;
  padding: 10px 18px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  font-family: inherit;
}
.c-nav-cta:hover {
  background: #007cb7;
}

/* ── Hero ── */
.c-hero {
  padding: 72px 48px 64px;
  border-bottom: 1px solid #edeff3;
}

.c-hero-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(280px, 420px);
  gap: 64px;
  align-items: center;
}
.c-hero-left {
  max-width: 720px;
}
.c-hero-right {
  display: flex;
  justify-content: center;
  align-items: center;
}
.c-mascot-wrap {
  position: relative;
  width: 100%;
  max-width: 420px;
  display: flex;
  justify-content: center;
}
.c-hero-mascot {
  width: 100%;
  max-width: 420px;
  height: auto;
  display: block;
  filter: drop-shadow(0 30px 40px rgba(14, 21, 36, 0.12));
  animation: cmascotFloat 6s ease-in-out infinite;
  position: relative;
  z-index: 1;
}
@keyframes cmascotFloat {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

/* Steam */
.c-steam {
  position: absolute;
  top: -80px;
  left: 50%;
  transform: translateX(-50%);
  width: 140px;
  height: 160px;
  pointer-events: none;
  z-index: 2;
}
.c-steam-puff {
  position: absolute;
  bottom: 0;
  left: 50%;
  width: 46px;
  height: 46px;
  border-radius: 50%;
  background: radial-gradient(
    circle at 35% 35%,
    rgba(150, 165, 190, 0.75) 0%,
    rgba(140, 155, 180, 0.5) 45%,
    rgba(140, 155, 180, 0) 75%
  );
  opacity: 0;
  filter: blur(5px);
  transform: translate(-50%, 0) scale(0.4);
  animation: csteamRise 4.2s ease-out infinite;
}
.c-steam-puff--1 {
  left: 48%;
  animation-delay: 0s;
}
.c-steam-puff--2 {
  left: 38%;
  animation-delay: 0.8s;
  width: 32px;
  height: 32px;
}
.c-steam-puff--3 {
  left: 58%;
  animation-delay: 1.6s;
  width: 44px;
  height: 44px;
}
.c-steam-puff--4 {
  left: 42%;
  animation-delay: 2.4s;
  width: 30px;
  height: 30px;
}
.c-steam-puff--5 {
  left: 54%;
  animation-delay: 3.2s;
  width: 36px;
  height: 36px;
}
@keyframes csteamRise {
  0% {
    opacity: 0;
    transform: translate(-50%, 20px) scale(0.4) rotate(0deg);
    filter: blur(3px);
  }
  15% {
    opacity: 0.95;
  }
  60% {
    opacity: 0.75;
    transform: translate(calc(-50% - 14px), -90px) scale(1.1) rotate(-12deg);
    filter: blur(7px);
  }
  100% {
    opacity: 0;
    transform: translate(calc(-50% + 18px), -180px) scale(1.6) rotate(10deg);
    filter: blur(14px);
  }
}

.c-h1 {
  font-family: "Playfair Display", serif;
  font-weight: 800;
  font-size: 96px;
  line-height: 0.98;
  letter-spacing: -0.035em;
  margin: 0 0 20px;
  text-wrap: balance;
}
.c-h1 em {
  font-style: italic;
  color: #0093e9;
  font-weight: 700;
}
.c-lede {
  font-size: 19px;
  line-height: 1.55;
  color: #4a5266;
  max-width: 62ch;
  margin: 0 0 32px;
}

.c-cta {
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-width: 540px;
}
.c-field {
  display: flex;
  background: #fff;
  border: 1px solid #d7dce5;
  border-radius: 12px;
  overflow: hidden;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.c-field:focus-within,
.c-field.c-field--focus {
  border-color: #0093e9;
  box-shadow: 0 0 0 4px rgba(0, 147, 233, 0.12);
}
.c-field input {
  flex: 1;
  padding: 14px 16px;
  border: 0;
  font-size: 15px;
  font-family: inherit;
  outline: none;
  background: transparent;
  color: #0e1524;
}
.c-field input::placeholder {
  color: #9aa3b2;
}
.c-field button {
  padding: 14px 20px;
  background: #0093e9;
  color: #fff;
  border: 0;
  font-weight: 600;
  font-size: 15px;
  cursor: pointer;
  font-family: inherit;
  white-space: nowrap;
}
.c-field button:hover:not(:disabled) {
  background: #007cb7;
}
.c-field button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.c-inline-msg {
  font-size: 13px;
  margin: 0;
}
.c-inline-msg--ok {
  color: #1a7f37;
}
.c-inline-msg--err {
  color: #a63318;
}

/* ── Promise / Mission ── */
.c-promise {
  padding: 64px 48px;
  border-bottom: 1px solid #edeff3;
  background: #0e1524;
  color: #f5f7fa;
}
.c-promise-mission {
  max-width: 920px;
  margin: 0 auto;
}
.c-promise-mission p {
  font-family: "Playfair Display", serif;
  font-weight: 500;
  font-size: 28px;
  line-height: 1.4;
  letter-spacing: -0.015em;
  margin: 0 0 18px;
  color: #f5f7fa;
  text-wrap: pretty;
}
.c-promise-mission p:last-child {
  margin-bottom: 0;
  color: rgba(245, 247, 250, 0.78);
}
.c-promise-mission em {
  font-style: italic;
  font-weight: 700;
  color: #f4b400;
}
.c-hl-blue {
  color: #5bb8f2;
  font-weight: 700;
  font-style: italic;
}
.c-hl-coral {
  color: #f08a6e;
  font-weight: 700;
  font-style: italic;
}
.c-hl-yellow {
  color: #f4b400;
  font-weight: 700;
  font-style: italic;
}

/* ── Today ── */
.c-today {
  padding: 72px 48px;
  border-bottom: 1px solid #edeff3;
}
.c-section-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 36px;
}
.c-eyebrow {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #0093e9;
  margin-bottom: 8px;
}
.c-section-head h2 {
  font-family: "Playfair Display", serif;
  font-weight: 800;
  font-size: 48px;
  line-height: 1;
  letter-spacing: -0.025em;
  margin: 0;
}
.c-total {
  font-size: 13px;
  color: #6e7890;
  background: #f5f7fa;
  padding: 8px 14px;
  border-radius: 999px;
  border: 1px solid #edeff3;
  font-weight: 500;
  white-space: nowrap;
}
.c-lead {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  align-items: center;
  text-decoration: none;
  color: inherit;
  margin-bottom: 48px;
  padding-bottom: 48px;
  border-bottom: 1px solid #edeff3;
}
.c-lead-img {
  aspect-ratio: 4 / 3;
  background: #e8eaee center / cover no-repeat;
  border-radius: 16px;
}
.c-row {
  display: flex;
  gap: 12px;
  align-items: center;
  margin-bottom: 14px;
}
.c-kicker {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #0093e9;
}
.c-kicker--alt {
  color: #6e7890;
}
.c-time {
  font-size: 12px;
  font-weight: 600;
  color: #4a5266;
  background: #f5f7fa;
  padding: 3px 10px;
  border-radius: 999px;
  border: 1px solid #edeff3;
}
.c-time--sm {
  font-size: 11px;
  padding: 2px 8px;
}
.c-lead h3 {
  font-family: "Playfair Display", serif;
  font-weight: 800;
  font-size: 36px;
  line-height: 1.08;
  letter-spacing: -0.02em;
  margin: 0 0 14px;
  text-wrap: balance;
}
.c-lead p {
  font-size: 16px;
  line-height: 1.55;
  color: #4a5266;
  margin: 0 0 16px;
}
.c-link {
  font-size: 14px;
  font-weight: 600;
  color: #0093e9;
}

.c-feed {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
}
.c-card {
  text-decoration: none;
  color: inherit;
  padding: 20px;
  border: 1px solid #edeff3;
  border-radius: 14px;
  background: #fff;
  transition: border-color 0.15s, transform 0.15s;
  display: block;
}
.c-card:hover {
  border-color: #0093e9;
  transform: translateY(-2px);
}
.c-card h4 {
  font-family: "Playfair Display", serif;
  font-weight: 700;
  font-size: 18px;
  line-height: 1.2;
  margin: 0 0 8px;
  letter-spacing: -0.01em;
}
.c-card p {
  margin: 0;
  font-size: 13.5px;
  line-height: 1.5;
  color: #6e7890;
}

/* ── Breakdown ── */
.c-breakdown {
  padding: 72px 48px;
  border-bottom: 1px solid #edeff3;
}
.c-breakdown h2 {
  font-family: "Playfair Display", serif;
  font-weight: 800;
  font-size: 48px;
  line-height: 1;
  letter-spacing: -0.025em;
  margin: 0 0 36px;
}
.c-break-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
}
.c-break {
  padding: 28px;
  border: 1px solid #edeff3;
  border-radius: 16px;
  background: #fbfbfc;
}
.c-break-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}
.c-break-time {
  font-family: "JetBrains Mono", monospace;
  font-size: 11px;
  font-weight: 700;
  padding: 5px 10px;
  border-radius: 6px;
  letter-spacing: 0.04em;
}
.c-break-tag {
  font-size: 11px;
  font-weight: 600;
  color: #6e7890;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.c-break h3 {
  font-family: "Playfair Display", serif;
  font-weight: 700;
  font-size: 22px;
  line-height: 1.15;
  margin: 0 0 8px;
  letter-spacing: -0.015em;
}
.c-break p {
  margin: 0;
  font-size: 14px;
  line-height: 1.55;
  color: #4a5266;
}

/* ── Stats ── */
.c-stats {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  background: #eaf4fe;
  border-bottom: 1px solid #edeff3;
}
.c-stats > div {
  padding: 40px 24px;
  text-align: center;
  border-right: 1px solid rgba(0, 147, 233, 0.15);
}
.c-stats > div:last-child {
  border-right: 0;
}
.c-stats b {
  display: block;
  font-family: "Playfair Display", serif;
  font-weight: 800;
  font-size: 54px;
  line-height: 0.95;
  letter-spacing: -0.03em;
  color: #0072b2;
  margin-bottom: 6px;
}
.c-stats b em {
  font-style: normal;
  font-size: 24px;
  color: #5f9cc7;
  font-weight: 600;
}
.c-stats span {
  font-size: 13px;
  color: #4a5266;
  font-weight: 500;
}

/* ── Testimonials ── */
.c-testi {
  padding: 72px 48px;
  border-bottom: 1px solid #edeff3;
}
.c-testi h2 {
  font-family: "Playfair Display", serif;
  font-weight: 800;
  font-size: 48px;
  line-height: 1;
  letter-spacing: -0.025em;
  margin: 0 0 36px;
}
.c-testi-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
}
.c-testi-card {
  margin: 0;
  padding: 28px;
  border: 1px solid #edeff3;
  border-radius: 16px;
  position: relative;
  background: #fff;
}
.c-quote-mark {
  position: absolute;
  top: 12px;
  right: 18px;
  font-family: "Playfair Display", serif;
  font-size: 80px;
  font-weight: 800;
  line-height: 1;
  opacity: 0.4;
}
.c-testi-card blockquote {
  margin: 0 0 20px;
  font-size: 16px;
  line-height: 1.55;
  color: #0e1524;
  font-family: "Playfair Display", serif;
  font-weight: 500;
}
.c-testi-card figcaption {
  font-size: 13px;
  color: #4a5266;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.c-testi-card figcaption b {
  color: #0e1524;
  font-weight: 600;
}

/* ── Final CTA ── */
.c-final {
  padding: 96px 48px;
  background: linear-gradient(180deg, #eaf4fe 0%, #f5faff 100%);
  border-bottom: 1px solid #edeff3;
  text-align: center;
}
.c-final-inner {
  max-width: 720px;
  margin: 0 auto;
}
.c-final h2 {
  font-family: "Playfair Display", serif;
  font-weight: 800;
  font-size: 64px;
  line-height: 1.02;
  letter-spacing: -0.03em;
  margin: 0 0 28px;
  text-wrap: balance;
}
.c-final h2 em {
  font-style: italic;
  color: #0093e9;
  font-weight: 700;
}
.c-field--light {
  background: #fff;
  box-shadow: 0 4px 20px rgba(14, 21, 36, 0.06);
}
.c-final-meta {
  margin-top: 16px;
  font-size: 13px;
  color: #4a5266;
}

/* ── Footer ── */
.c-foot {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 28px 48px;
  gap: 24px;
  flex-wrap: wrap;
}
.c-foot-brand {
  display: flex;
  gap: 10px;
  align-items: center;
  font-weight: 700;
  font-size: 14px;
  cursor: pointer;
}
.c-foot nav {
  display: flex;
  gap: 24px;
  font-size: 13px;
  color: #4a5266;
}
.c-foot nav a {
  color: inherit;
  text-decoration: none;
  cursor: pointer;
}
.c-foot nav a:hover {
  color: #0093e9;
}
.c-foot-legal {
  font-size: 12px;
  color: #6e7890;
}

/* ── Responsive ── */
@media (max-width: 900px) {
  .c-nav {
    padding: 16px 24px;
  }
  .c-nav-links {
    display: none;
  }
  .c-hero {
    padding: 48px 24px 40px;
  }
  .c-hero-grid {
    grid-template-columns: 1fr;
    gap: 32px;
  }
  .c-hero-right {
    order: -1;
  }
  .c-mascot-wrap,
  .c-hero-mascot {
    max-width: 280px;
  }
  .c-h1 {
    font-size: 52px;
  }
  .c-promise {
    padding: 48px 24px;
  }
  .c-today,
  .c-breakdown,
  .c-testi {
    padding: 48px 24px;
  }
  .c-lead {
    grid-template-columns: 1fr;
    gap: 20px;
  }
  .c-feed {
    grid-template-columns: 1fr 1fr;
  }
  .c-break-grid {
    grid-template-columns: 1fr;
  }
  .c-stats {
    grid-template-columns: repeat(2, 1fr);
  }
  .c-stats > div:nth-child(2) {
    border-right: 0;
  }
  .c-testi-grid {
    grid-template-columns: 1fr;
  }
  .c-final {
    padding: 64px 24px;
  }
  .c-final h2 {
    font-size: 40px;
  }
  .c-foot {
    flex-direction: column;
    align-items: flex-start;
    padding: 24px;
    gap: 16px;
  }
  .c-foot nav {
    flex-wrap: wrap;
    gap: 16px;
  }
}

@media (max-width: 480px) {
  .c-feed {
    grid-template-columns: 1fr;
  }
  .c-stats {
    grid-template-columns: repeat(2, 1fr);
  }
}

/* ── Wide-screen containment ── */
.c-nav,
.c-hero,
.c-promise,
.c-today,
.c-breakdown,
.c-stats,
.c-testi,
.c-final,
.c-foot {
  padding-left: max(48px, calc((100% - 1200px) / 2));
  padding-right: max(48px, calc((100% - 1200px) / 2));
}
</style>
